"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, ShieldCheck, Scale, Landmark, Award } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
const pillars = [{
  title: "Gobierno y administración pública",
  description: "Contabilidad gubernamental, armonización CONAC y respaldo en licitaciones estatales y municipales.",
  icon: Landmark
}, {
  title: "Empresas e industrias",
  description: "Estrategia fiscal, asesoría administrativa, auditorías y cumplimiento preventivo para el sector productivo.",
  icon: ShieldCheck
}, {
  title: "Consultoría jurídica especializada",
  description: "Defensa y orientación técnica en derecho administrativo, fiscal, corporativo y agrario.",
  icon: Scale
}];
export default function About() {
  const shouldReduceMotion = useReducedMotion();
  const transitionSettings = shouldReduceMotion ? {
    duration: 0
  } : {
    duration: 0.5,
    ease: [0.16, 1, 0.3, 1]
  };
  return <section id="quiénes-somos" data-nav="light" className="relative bg-background text-foreground py-20 lg:py-28 overflow-hidden border-b border-border">
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
        {/* Top Eyebrow and Exact Motto */}
        <div className="mb-14">
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 12
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={transitionSettings} className="flex flex-wrap items-center gap-3 text-xs tracking-widest uppercase font-semibold font-serif text-foreground/80 mb-3">
            <span className="inline-flex items-center px-2.5 py-1 rounded-sm border border-ring/40 bg-accent text-accent-foreground text-[11px] font-bold tracking-wider">
              ASV CONSULTORES
            </span>
            <span className="text-ring font-bold">·</span>
            <span className="text-muted-foreground">Firma multidisciplinaria</span>
          </motion.div>

          <motion.div initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 12
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          ...transitionSettings,
          delay: 0.08
        }} className="flex flex-col md:flex-row md:items-end justify-between gap-6">
            <div>
              <p className="text-sm md:text-base font-serif uppercase tracking-wider text-muted-foreground mb-2">
                Estrategia · Cumplimiento · Resultados
              </p>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-serif font-bold tracking-tight text-foreground uppercase">
                Quiénes somos
              </h2>
            </div>

            {/* ASV Gold Hairline */}
            <div className="hidden md:block w-24 h-[2px] bg-ring mb-3" />
          </motion.div>
          <div className="md:hidden mt-4 w-12 h-[2px] bg-ring" />
        </div>

        {/* 2-Column Split: Trajectory & Image */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          {/* Left Column: Narrative */}
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 12
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          ...transitionSettings,
          delay: 0.12
        }} className="lg:col-span-7 flex flex-col space-y-6">
            <p className="text-lg md:text-xl font-sans text-foreground/90 leading-relaxed font-normal">
              ASV Consultores es una firma mexicana de consultoría profesional organizada en tres áreas
              estratégicas: Gobierno, Empresa y Jurídico. Dirigida por el Director General L.C.P. Jesús Adán
              Avilez Soto, nuestra institución consolida equipos de alta especialidad técnica para respaldar a
              organismos públicos y empresas en todo el país.
            </p>

            <p className="text-base text-muted-foreground font-sans leading-relaxed">
              Nacemos con el compromiso de transformar retos operativos, contables y regulatorios en
              estructuras sólidas de cumplimiento normativo y certeza financiera. Con sede en Los Mochis, Sinaloa,
              articulamos soluciones integrales de alto nivel que integran contabilidad gubernamental, armonización
              CONAC, blindaje fiscal y defensa jurídica.
            </p>

            {/* Leadership Highlight Badge */}
            <div className="border border-border bg-card p-5 rounded-sm flex items-start gap-4 shadow-xs">
              <div className="p-2.5 rounded-sm bg-primary text-primary-foreground shrink-0 mt-0.5">
                <Award className="size-5 text-ring" />
              </div>
              <div className="space-y-1">
                <h3 className="text-sm font-serif font-semibold text-card-foreground tracking-wide uppercase">
                  Dirección y supervisión técnica senior
                </h3>
                <p className="text-xs md:text-sm text-muted-foreground leading-relaxed">
                  Cada proyecto cuenta con el aval directo de especialistas titulares y la conducción del L.C.P.
                  Jesús Adán Avilez Soto, garantizando rigor documental, apego estricto a las normas y resultados
                  medibles.
                </p>
              </div>
            </div>

            {/* Core Pillars List */}
            <div className="space-y-4 pt-4 border-t border-border">
              {pillars.map((pillar, i) => {
              const Icon = pillar.icon;
              return <div key={pillar.title} data-index={i} className="flex items-start gap-3.5 group">
                    <div className="p-1.5 rounded-xs bg-muted text-foreground shrink-0 mt-0.5 border border-border group-hover:border-ring transition-colors">
                      <Icon className="size-4 text-foreground" />
                    </div>
                    <div>
                      <h4 className="text-sm font-serif font-semibold text-foreground tracking-normal">
                        {pillar.title}
                      </h4>
                      <p className="text-xs md:text-sm text-muted-foreground leading-normal mt-0.5">
                        {pillar.description}
                      </p>
                    </div>
                  </div>;
            })}
            </div>

            {/* Action Buttons */}
            <div className="pt-4 flex flex-wrap items-center gap-4">
              <Button asChild className="bg-primary text-primary-foreground hover:bg-ring hover:text-primary transition-colors border border-ring font-serif uppercase text-xs tracking-wider font-semibold px-6 py-2.5 rounded-xs h-auto shadow-xs">
                <Link to="/ContactPage">
                  Solicitar asesoría
                </Link>
              </Button>

              <Button asChild variant="outline" className="border-primary text-primary hover:bg-primary/5 transition-colors font-serif uppercase text-xs tracking-wider font-semibold px-6 py-2.5 rounded-xs h-auto">
                <Link to="/ProductsAndServicesPage" className="inline-flex items-center gap-2">
                  <span>Explorar áreas</span>
                  <ArrowRight className="size-3.5 text-ring" />
                </Link>
              </Button>
            </div>
          </motion.div>

          {/* Right Column: Imagery & Institutional Card */}
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 12
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          ...transitionSettings,
          delay: 0.18
        }} className="lg:col-span-5 relative">
            <div className="relative rounded-xs border border-border bg-card overflow-hidden shadow-md">
              {/* Gold Top Accent Hairline */}
              <div className="h-[2px] w-full bg-ring" />

              <div className="relative aspect-[4/3] w-full overflow-hidden bg-muted">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1799246/img/asv_corporate_boardroom_collaboration.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1799246/img/asv_corporate_boardroom_collaboration.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1799246/img/asv_corporate_boardroom_collaboration.png-wvc-sizes" alt="Equipo de consultores profesionales en sesión técnica corporativa de ASV Consultores" loading="lazy" decoding="async" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-primary/10" />
              </div>

              {/* Office Details & Stance */}
              <div className="p-6 space-y-3 bg-card text-card-foreground">
                <div className="flex items-center justify-between text-xs text-muted-foreground uppercase tracking-wider font-serif">
                  <span>Presencia institucional</span>
                  <span>Sinaloa, México</span>
                </div>
                <h3 className="text-base font-serif font-bold text-card-foreground uppercase tracking-tight">
                  Atención integral en todo el territorio nacional
                </h3>
                <p className="text-xs md:text-sm text-muted-foreground leading-relaxed">
                  Ofrecemos atención presencial y consultoría remota en tiempo real para dependencias
                  gubernamentales, consorcios empresariales y despachos jurídicos con requerimientos de alta
                  complejidad.
                </p>

                <div className="pt-2 border-t border-border flex items-center justify-between">
                  <span className="text-xs font-serif font-medium text-foreground">
                    Sede ejecutiva Los Mochis
                  </span>
                  <Link to="/ContactPage" className="text-xs font-serif font-semibold text-foreground hover:text-ring transition-colors inline-flex items-center gap-1">
                    <span>Contactar</span>
                    <ArrowRight className="size-3 text-ring" />
                  </Link>
                </div>
              </div>
            </div>

            {/* Subtle framing watermark border */}
            <div className="hidden sm:block absolute -bottom-3 -right-3 w-full h-full border border-border -z-10 rounded-xs pointer-events-none" />
          </motion.div>
        </div>
      </div>
    </section>;
}